import { formatAmounts } from '@aytm/app/stats/lib/util/helpers'
import { buildColumnBarIngot } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/columnBarConfig'
import {
  calcMinMax,
  calcTickPositions
} from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/calcMinMax'
import { getErrData } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/errData'
import { linkCellFromPoint } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/pointHalo'
import { sortData } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/sortData'
import {
  removeLogicAndTruncate,
  transformText
} from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/textUtils'
import { wireMoEEvents } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/marginOfError'
import { CHART_HEIGHT } from '@aytm/app/stats/page/question-views/components/lib/constants'

// Data Lab Columns chart — multi-batch grouped/interlaced × column/bar with errData/sigtest.
// Ported from app/_stock/coffeescripts/app/stats/highchart_models/data_lab/columns.coffee
// MoE hover overlay is intentionally NOT implemented here (Phase 2 — see plan 01 hook points).
// errData on points is still prepared in full so Phase 2 can plug in without changes here.

const CHART_MARGIN = [124, 62, 30, 85]
const MAX_AV_FOR_LEGEND_LONG = 12
const SPECIAL_QTYPES = ['chart', 'bars', 'stacked-bars']

// The chart is asked for by the key the builder registry is indexed under, which carries a family
// prefix, while the four modes below are named bare. Reading the mode off the key rather than
// comparing to it keeps both spellings working: a caller that hands over the bare name -- as the
// unit tests do -- is answered the same as the shell, which hands over the key.
const bareMode = chartMode => String(chartMode || '').replace(/^dl_/, '')

const isUnstackedMode = chartMode => ['columns', 'bcolumns'].includes(bareMode(chartMode))
const isStackedMode = chartMode => ['stacked', 'bstacked'].includes(bareMode(chartMode))
const isVerticalMode = chartMode => ['columns', 'stacked'].includes(bareMode(chartMode))
const isBarMode = chartMode => ['bcolumns', 'bstacked'].includes(bareMode(chartMode))

const getMode = (axisType, answerType) => (axisType === 'respondents' ? answerType || 'amount' : 'percents')

const getAnswerText = answerType => (answerType === 'amount' ? 'respondent' : answerType)

const getPercentDecimals = (format, reportPercentDecimals) =>
  format && Object.hasOwn(format, 'percentDecimals') ? format.percentDecimals : (reportPercentDecimals ?? 1)

const buildDataLabIngot = props => {
  const data = buildColumnBarIngot(props)

  // buildColumnBarIngot sets `plotOptions.series.stacking = 'normal'` (harmless for single-series
  // charts, but data_lab has multi-series). Reset so applyColumn/applyBar fully control stacking.
  if (data.plotOptions?.series) {
    data.plotOptions.series.stacking = undefined
  }

  if (data.chart) {
    data.chart.margin = [...CHART_MARGIN]
  }

  // data_lab legend — left-aligned strip at the top (coffee:338-344)
  if (data.legend) {
    Object.assign(data.legend, {
      align: 'left',
      backgroundColor: 'rgba(256,256,256,0.4)',
      maxHeight: 34,
      verticalAlign: 'top',
      width: 630,
      x: 22
    })
  }

  // data_lab title — left-aligned with offset (coffee:418-426)
  if (data.title) {
    Object.assign(data.title, {
      align: 'left',
      margin: 34,
      style: {
        color: '#333333',
        fontFamily: 'Ubuntu, Calibri, Segoe, Helvetica, Arial',
        fontSize: '14px'
      },
      x: 24,
      y: 44
    })
  }

  return data
}

// Builds tooltip desc with the literal {err} placeholder; the tooltip formatter
// (from buildColumnBarIngot) replaces {err} with " (±X.XX%)" at render time.
const buildDescription = ({
  as,
  amount,
  b_text,
  av_text,
  b,
  av,
  answerType,
  answerText,
  ignoreAmount,
  ignorePercents,
  ignoreRefs,
  percentDecimals
}) => {
  let desc = !ignorePercents
    ? `<div><b>${formatAmounts(as, 'percents', percentDecimals)}</b>%{err}</div>`
    : ignoreAmount
      ? '<div>{err}</div>'
      : ''

  if (!ignoreAmount) {
    const unitSuffix = answerType === 'amount' ? ' ' + answerText + (Number(amount) !== 1 ? 's' : '') : ''
    const respInfo = `<b>${amount}</b>${unitSuffix}`
    desc += ignorePercents ? `<div>${respInfo}{err}</div>` : `<div>${respInfo}</div>`
  }

  const bPrefix = ignoreRefs || !b.prefix ? '' : b.prefix + ': '
  const avPrefix = ignoreRefs || !av.prefix ? '' : av.prefix + ': '

  desc += `<div class='highcharts-tooltip-text'><b>${bPrefix}${b_text}</b></div>`
  desc += `<div class='highcharts-tooltip-text'><b>${avPrefix}</b>${av_text}</div>`

  return desc
}

// Builds the pointData object (id, name, x, y, errData) for one (batch, av) pair.
// errData.i = b.position - 1 — BATCH index — same for both groupType
// (critical for sigtest cross-batch comparison in MoE Phase 2).
const buildPoint = ({
  b,
  av,
  as,
  x,
  mode,
  columnsMode,
  answer,
  answerType,
  format,
  rType,
  ignoreAmount,
  ignorePercents,
  confidenceLevels,
  desc
}) => {
  const errResult = columnsMode
    ? getErrData(as, mode, { ignoreAmount, ignorePercents, rType, answerType, confidenceLevels })
    : { err: null, minErrValue: 0, maxErrValue: 0 }

  const pointData = {
    id: `b${b.position}a${av.position}`,
    name: desc,
    x,
    y: as[mode],
    errData: {
      i: b.position - 1,
      err: errResult.err,
      [answerType]: parseFloat(as[answerType]),
      answer: as,
      total: (answer[`b${b.position}`] || {}).total || 0
    }
  }

  if (Object.hasOwn(as, 'sd')) {
    pointData.errData.sd = as.sd
    pointData.errData.mu = Object.hasOwn(as, 'mu') ? as.mu : as[answerType]
  }

  if (format && format.useSigTest === true) {
    pointData.errData.sigtest = as.sigtest
  }

  return { pointData, minErrValue: errResult.minErrValue, maxErrValue: errResult.maxErrValue }
}

// groupType='interlaced': categories = answer variants of batches[0]; series = batches.
const calcInterlaced = (data, props, batches, mode, columnsMode) => {
  const {
    qType,
    answer = {},
    answerType = 'amount',
    chartMode,
    format = {},
    ignoreAmount,
    ignorePercents,
    ignoreRefs,
    rType,
    confidenceLevels,
    width = 658
  } = props
  const themeColors = props.colorTheme || []
  const answerText = getAnswerText(answerType)
  const percentDecimals = getPercentDecimals(format, props.reportPercentDecimals)
  const stackedMode = isStackedMode(chartMode)

  let chartLegendMode = props.chartLegendMode

  let minYValue = 0
  let maxYValue = 0

  const firstBatchAvs = batches[0].answerVariants || []
  const valueArr = Array.from({ length: firstBatchAvs.length }, () => 0)

  // Categories from batches[0].answerVariants (or single empty for special-qType single-AV case).
  const categories = []
  const useNormalCategories = firstBatchAvs.length > 1 || !SPECIAL_QTYPES.includes(qType)

  if (useNormalCategories) {
    // Downgrade chartLegendMode to 'short' for vertical column modes when > 12 AVs (coffee:495-497).
    if (isVerticalMode(chartMode) && firstBatchAvs.length > MAX_AV_FOR_LEGEND_LONG) {
      chartLegendMode = 'short'
    }
    for (const av of firstBatchAvs) {
      if (av == null) continue
      const _textSize = isBarMode(chartMode) ? 12 : (80 * width) / (746 * firstBatchAvs.length)
      const av_text =
        ignoreRefs || !av.prefix
          ? transformText(av.text, _textSize)
          : chartLegendMode === 'short'
            ? av.prefix
            : transformText(`${av.prefix}: ${av.text}`, _textSize)
      categories.push(av_text)
    }
  } else {
    categories.push('')
  }
  if (batches.length === 1 && SPECIAL_QTYPES.includes(qType)) {
    data.legend.enabled = false
  }
  data.xAxis.categories = categories

  // Series per batch (one series per batch, one data point per AV of that batch).
  data.series = []
  for (let bi = 0; bi < batches.length; bi++) {
    const b = batches[bi]
    const b_text = removeLogicAndTruncate(b.text, 100)
    const series = []

    for (let avi = 0; avi < b.answerVariants.length; avi++) {
      const av = b.answerVariants[avi]
      if (!av) continue
      const av_text = removeLogicAndTruncate(av.text, 100)

      const as = Object.assign({ [answerType]: 0, percents: 0 }, answer[`b${b.position}`]?.[`a${av.position}`] || {})
      const amount = formatAmounts(as[answerType])

      const desc = buildDescription({
        as,
        amount,
        b_text,
        av_text,
        b,
        av,
        answerType,
        answerText,
        ignoreAmount,
        ignorePercents,
        ignoreRefs,
        percentDecimals
      })

      const { pointData, minErrValue, maxErrValue } = buildPoint({
        b,
        av,
        as,
        x: av.position - 1,
        mode,
        columnsMode,
        answer,
        answerType,
        format,
        rType,
        ignoreAmount,
        ignorePercents,
        confidenceLevels,
        desc
      })

      series.push(pointData)

      if (columnsMode) {
        minYValue = Math.min(minYValue, as[mode] - (minErrValue || 0))
        maxYValue = Math.max(maxYValue, as[mode] + (maxErrValue || 0))
      }
      if (stackedMode) {
        valueArr[avi] = (valueArr[avi] || 0) + as[mode]
      }
    }

    const legendText =
      ignoreRefs || !b.prefix
        ? removeLogicAndTruncate(`${b_text}`, 27)
        : chartLegendMode === 'short'
          ? b.prefix
          : removeLogicAndTruncate(`${b.prefix}: ${b_text}`, 27)

    data.series.push({
      id: `b${b.position}`,
      name: legendText,
      color: themeColors[bi % themeColors.length],
      colorIndex: bi,
      data: series
    })
  }

  return { minYValue, maxYValue, valueArr }
}

// groupType='grouped': categories = batches; series = answer variants of batches[0].
const calcGrouped = (data, props, batches, mode, columnsMode) => {
  const {
    qType,
    answer = {},
    answerType = 'amount',
    chartMode,
    format = {},
    ignoreAmount,
    ignorePercents,
    ignoreRefs,
    rType,
    confidenceLevels,
    width = 658
  } = props
  const themeColors = props.colorTheme || []
  const answerText = getAnswerText(answerType)
  const percentDecimals = getPercentDecimals(format, props.reportPercentDecimals)
  const stackedMode = isStackedMode(chartMode)

  let chartLegendMode = props.chartLegendMode

  let minYValue = 0
  let maxYValue = 0

  const firstBatchAvs = batches[0].answerVariants || []
  const valueArr = Array.from({ length: batches.length }, () => 0)

  // Categories from batches (or single empty for special-qType single-batch case).
  const categories = []
  const useNormalCategories = batches.length > 1 || !SPECIAL_QTYPES.includes(qType)

  if (useNormalCategories) {
    if (isVerticalMode(chartMode) && batches.length > MAX_AV_FOR_LEGEND_LONG) {
      chartLegendMode = 'short'
    }
    const _textSize = isBarMode(chartMode) ? 12 : (80 * width) / (746 * batches.length)
    for (const b of batches) {
      const b_text =
        ignoreRefs || !b.prefix
          ? transformText(b.text, _textSize)
          : chartLegendMode === 'short'
            ? b.prefix
            : transformText(`${b.prefix}: ${b.text}`, _textSize)
      categories.push(b_text)
    }
  } else {
    categories.push('')
  }
  if (firstBatchAvs.length === 1 && SPECIAL_QTYPES.includes(qType)) {
    data.legend.enabled = false
  }
  data.xAxis.categories = categories

  // Series per AV (one series per AV of batches[0], one data point per batch).
  data.series = []
  for (let avi = 0; avi < firstBatchAvs.length; avi++) {
    const av = firstBatchAvs[avi]
    if (!av) continue
    const av_text = removeLogicAndTruncate(av.text, 100)
    const series = []

    for (let bi = 0; bi < batches.length; bi++) {
      const b = batches[bi]
      const b_text = removeLogicAndTruncate(b.text, 100)

      const as = Object.assign({ [answerType]: 0, percents: 0 }, answer[`b${b.position}`]?.[`a${av.position}`] || {})
      const amount = formatAmounts(as[answerType])

      const desc = buildDescription({
        as,
        amount,
        b_text,
        av_text,
        b,
        av,
        answerType,
        answerText,
        ignoreAmount,
        ignorePercents,
        ignoreRefs,
        percentDecimals
      })

      const { pointData, minErrValue, maxErrValue } = buildPoint({
        b,
        av,
        as,
        x: b.position - 1,
        mode,
        columnsMode,
        answer,
        answerType,
        format,
        rType,
        ignoreAmount,
        ignorePercents,
        confidenceLevels,
        desc
      })

      series.push(pointData)

      if (columnsMode) {
        minYValue = Math.min(minYValue, as[mode] - (minErrValue || 0))
        maxYValue = Math.max(maxYValue, as[mode] + (maxErrValue || 0))
      }
      if (stackedMode) {
        valueArr[bi] = (valueArr[bi] || 0) + as[mode]
      }
    }

    const legendText =
      ignoreRefs || !av.prefix
        ? removeLogicAndTruncate(`${av_text}`, 27)
        : chartLegendMode === 'short'
          ? av.prefix
          : removeLogicAndTruncate(`${av.prefix}: ${av_text}`, 27)

    data.series.push({
      id: `av${av.position}`,
      name: legendText,
      color: themeColors[avi % themeColors.length],
      colorIndex: avi,
      data: series
    })
  }

  return { minYValue, maxYValue, valueArr }
}

const applyAxisAndLimits = (data, props, ctx) => {
  const {
    axisType,
    chartMode,
    ignorePercents,
    answerType = 'amount',
    xAxisTitle,
    yAxisTitle,
    textTitle,
    text,
    groupType = 'interlaced'
  } = props
  const { valueArr } = ctx

  let minYValue = ctx.minYValue
  let maxYValue = ctx.maxYValue
  const stackedMode = isStackedMode(chartMode)
  const answerText = getAnswerText(answerType)

  // Stacked: replace per-point accumulation with totals from valueArr; reverse series order.
  if (stackedMode) {
    minYValue = Math.min.apply(Math, valueArr)
    maxYValue = Math.max.apply(Math, valueArr)
    data.legend.reversed = true
    data.series.reverse()
  }

  // yAxis.min: negative → -ceil(|min|), else 0.
  data.yAxis.min = minYValue < 0 ? -Math.ceil(Math.abs(minYValue)) : 0

  // yAxis.max: 3-branch formula (coffee:655).
  if (maxYValue <= 0 && minYValue >= 0) {
    data.yAxis.max = 100
  } else if (maxYValue <= 0) {
    data.yAxis.max = Math.ceil(Math.abs(minYValue))
  } else {
    data.yAxis.max = Math.ceil(maxYValue)
  }

  // Tick positions: unstacked goes through calcMinMax (handles yAxisLimits/zoomType); stacked uses calcTickPositions.
  if (stackedMode) {
    // A stack shorter than the whole is still read against the whole, so the percentage axis keeps
    // its full height rather than closing in on the tallest stack. It only ever grows from here --
    // a pair summing past 100 is drawn to its own total. Unstacked charts get this from calcMinMax,
    // which a stacked one does not go through.
    if ('percents' === axisType && !ignorePercents) data.yAxis.max = Math.max(data.yAxis.max, 100)

    data.yAxis.tickPositions = calcTickPositions(data.yAxis.min, data.yAxis.max)
  } else {
    calcMinMax(data, !ignorePercents && axisType === 'percents', {
      answer: props.answer || {},
      rType: props.rType,
      yAxisLimits: props.yAxisLimits,
      zoomType: props.zoomType
    })
  }

  // Axis titles. xAxisTitle: unconditional. yAxisTitle: ONLY for axisType='respondents' (for percents — always '%').
  data.yAxis.title.text = axisType === 'respondents' ? `${answerText}s` : '%'
  if (xAxisTitle != null) data.xAxis.title.text = xAxisTitle
  if (yAxisTitle != null && axisType === 'respondents') data.yAxis.title.text = yAxisTitle

  // Title: textTitle overrides; text can be a tuple [groupedText, interlacedText].
  if (textTitle != null) {
    data.title.text = textTitle
  } else {
    const picked = Array.isArray(text) ? text[groupType === 'interlaced' ? 1 : 0] : text
    data.title.text = removeLogicAndTruncate(picked || '', 100)
  }
  if (!data.title.text.length) data.legend.y = 40
}

// Step 4.5: y-clamp post-pass (does NOT affect yAxis) and exporting wiring.
const applyPostProcessing = (data, props) => {
  for (const s of data.series || []) {
    for (const d of s.data || []) {
      if (d == null) continue
      if (d.y < 0.005 && d.y >= 0) d.y = 0.005
      if (d.y > -0.005 && d.y < 0) d.y = -0.005
    }
  }

  if (data.exporting && props.exportFileName) {
    data.exporting.width = props.width
    data.exporting.filename = props.exportFileName
  }
}

// Vertical column. `kind` = 'unstacked' | 'stacked' (stacking strategy, NOT props.groupType).
const applyColumn = (result, props, kind) => {
  const { options } = result

  if (options.chart) {
    options.chart.width = props.width
    options.chart.height = 20 + (props.height || CHART_HEIGHT)
    options.chart.type = 'column'
  }

  options.plotOptions = options.plotOptions || {}
  options.plotOptions.column = options.plotOptions.column || {}
  options.plotOptions.column.groupPadding = 0.05
  if (kind === 'stacked') options.plotOptions.column.stacking = 'normal'

  return result
}

// Horizontal bar with dynamic height. NO `barsCount > 7` guard — formula applies always
// (for barsCount < 7 height SHRINKS, intentional — matches coffee:720-728).
const applyBar = (result, props, kind) => {
  const { options } = result

  if (options.chart) {
    options.chart.width = props.width
    options.chart.height = 20 + (props.height || CHART_HEIGHT)
    options.chart.type = 'bar'
    options.chart.marginRight = 43
    options.chart.marginBottom = 50
    options.chart.marginLeft = 130
  }

  if (options.xAxis?.labels) {
    delete options.xAxis.labels.step
    delete options.xAxis.labels.y
  }

  options.plotOptions = options.plotOptions || {}
  options.plotOptions.bar = options.plotOptions.bar || {}
  options.plotOptions.bar.groupPadding = 0.05
  if (kind === 'stacked') options.plotOptions.bar.stacking = 'normal'

  const seriesCount = options.series?.length || 0
  const dataLen = options.series?.[0]?.data?.length || 0
  const barsCount = (kind === 'stacked' ? 1 : seriesCount) * dataLen
  const chartHeight = options.chart?.height ?? 0

  if (Number.isFinite(barsCount) && Number.isFinite(chartHeight) && barsCount > 0) {
    const stackedOrShort = kind === 'stacked' || seriesCount < 2 || barsCount < 12
    const correctedHeight = chartHeight + (stackedOrShort ? 40 : 12) * (barsCount - 7)
    options.chart.height = correctedHeight
    if (options.chart.backgroundColor?.linearGradient) {
      options.chart.backgroundColor.linearGradient = [0, 0, 0, correctedHeight]
    }
  }

  return result
}

const buildBaseDataLabOptions = props => {
  const data = buildDataLabIngot(props)

  // A block that draws the crossed pair as a grid as well as a chart hands in its own id, and the
  // point under the pointer tints the cell standing for the same pair. Set on plotOptions before
  // the MoE wiring runs, because that wiring chains whatever it finds there ahead of its own
  // handlers -- setting it after would drop the confidence overlay without a word.
  if (props.linkCells) {
    data.aytmCellBlock = props.linkCells
    data.plotOptions.series.point = {
      ...data.plotOptions.series.point,
      events: {
        ...data.plotOptions.series.point?.events,
        mouseOut() {
          linkCellFromPoint(props.linkCells, null)
        },
        mouseOver() {
          linkCellFromPoint(props.linkCells, this.id)
        }
      }
    }
  }

  const { groupType = 'interlaced', chartMode, axisType, answerType = 'amount' } = props
  const mode = getMode(axisType, answerType)
  const columnsMode = isUnstackedMode(chartMode)

  // Shallow-copy batches + sort each batch's answerVariants by its own answer data.
  // We never mutate React props.
  const rawBatches = props.batches || []
  const batches = rawBatches.map(b => ({
    ...b,
    answerVariants: sortData(
      props.answer?.[`b${b.position}`] || {},
      (b.answerVariants || []).slice(),
      props.sortOrder,
      { answerType }
    )
  }))

  if (!batches.length || !batches[0]) {
    return { options: data }
  }

  const ctx =
    groupType === 'interlaced'
      ? calcInterlaced(data, props, batches, mode, columnsMode)
      : calcGrouped(data, props, batches, mode, columnsMode)

  applyAxisAndLimits(data, props, ctx)
  applyPostProcessing(data, props)

  return { options: data }
}

// Unstacked builders wire MoE (errData exists only in columnsMode); stacked do NOT (err = null).
export const buildDataLabColumnsOptions = props => {
  const result = applyColumn(buildBaseDataLabOptions(props), props, 'unstacked')
  wireMoEEvents(result.options, props)
  return result
}

export const buildDataLabStackedOptions = props => applyColumn(buildBaseDataLabOptions(props), props, 'stacked')

export const buildDataLabBColumnsOptions = props => {
  const result = applyBar(buildBaseDataLabOptions(props), props, 'unstacked')
  wireMoEEvents(result.options, props)
  return result
}

export const buildDataLabBStackedOptions = props => applyBar(buildBaseDataLabOptions(props), props, 'stacked')
